import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import dateFormat from 'dateformat';
import Button from 'reactstrap/lib/Button';
import { get } from 'lodash-es';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VisitorModel } from 'client/data/models/visitor';
import { FeatureSpecsPaths, VehicleModel } from 'client/data/models/vehicle-v2';
import { InventoryTrimSummaryEntities } from 'client/data/models/inventory-core-configurations';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import {
  getEspLabelFromTrim,
  getSuggestedDisplayPriceFromTrim,
} from 'site-modules/shared/utils/core-page/suggested-price';
import { getFinalAssemblyCountry } from 'site-modules/shared/utils/features-specs/utils';
import { getEspTrustDateUnformatted } from 'site-modules/shared/utils/esp';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { buildLeadTargetsPath, LeadTargetsModel } from 'client/data/models/lead-targets';
import { STRATEGIES } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { getPlural } from 'client/utils/plural';
import { isRoiTargetedFlavor } from 'client/utils/flavor-targeting';

const CREATIVE_ID = { CTA: 'edm-entry-model-summary-cta', SUMMARY: 'edm-entry-model-summary' };

function TrimsCtaSimpleSummaryUI({
  className = null,
  selectedTrim,
  finalAssemblyCountry = null,
  belowEspLeadTargetsNum = null,
  stateCode = null,
  vehicleName,
  vehicleParams,
  ctaProps = null,
  showLeadForm = false,
  isDealerlessOem = false,
}) {
  const ctaText = get(ctaProps, 'children');
  useEffect(() => {
    if (!ctaText) {
      return;
    }

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: 'trim_summary_cta_text',
        creative_id: CREATIVE_ID.CTA,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        value: ctaText,
      },
    });
  }, [ctaText]);

  const finalAssemblyCountryText = finalAssemblyCountry && `Final assembly in ${finalAssemblyCountry}`;
  useEffect(() => {
    if (!finalAssemblyCountryText) {
      return;
    }

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: 'country_of_final_assembly',
        creative_id: CREATIVE_ID.SUMMARY,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        value: finalAssemblyCountryText,
      },
    });
  }, [finalAssemblyCountryText]);

  useEffect(() => {
    if (!belowEspLeadTargetsNum) {
      return;
    }

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: 'lower-dealer-price-found',
        creative_id: CREATIVE_ID.SUMMARY,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        value: belowEspLeadTargetsNum,
      },
    });
  }, [belowEspLeadTargetsNum]);

  const totalMsrp = get(selectedTrim, 'pricing.totalMsrp');
  const ESP = get(selectedTrim, 'pricing.suggestedPrice');
  const displayPrice = getSuggestedDisplayPriceFromTrim(selectedTrim);
  const espLabel = getEspLabelFromTrim(selectedTrim);

  const totalInventory = get(selectedTrim, 'inventoryCount');
  const buttonProps = {
    ...ctaProps,
    size: 'lg',
    color: 'blue-50',
    className: 'cta-button rounded-8 w-100 py-0_75 text-transform-none text-white',
    'data-hidedelightedoninteraction': true,
  };

  return (
    <div className={classnames('trim-simple-summary size-16 w-100', className)} data-nosnippet>
      <div className="px-0_25">
        <div className="d-flex flex-column align-items-center justify-content-between">
          <div className="fw-medium mb-0_25">{espLabel}</div>
          <strong className="heading-2 d-flex align-items-center">
            {getPriceString(displayPrice)}
            {!ESP && (
              <PopoverItem id="no-esp" className="ms-0_25 text-primary-darker" screenReaderTooltipLabel={espLabel}>
                Edmunds does not currently have enough transaction data to determine a Suggested Price for the{' '}
                {vehicleParams.year} {vehicleParams.makeName} {vehicleParams.modelName}.
              </PopoverItem>
            )}
          </strong>
        </div>
        {ESP > totalMsrp && (
          <div className="small text-cool-gray-40 text-center mt-0_25">
            Low supply is pushing the market average above MSRP.
          </div>
        )}
      </div>
      {!!ctaProps && (
        <Fragment>
          {showLeadForm ? (
            <Fragment>
              {!!belowEspLeadTargetsNum && (
                <div className="text-success text-center fw-medium pos-a center-x w-100">
                  {belowEspLeadTargetsNum} lower dealer {getPlural('price', belowEspLeadTargetsNum)} found!
                </div>
              )}
              <Button
                {...buttonProps}
                className={classnames('summary-cta lead-form-button mt-2', buttonProps.className)}
                data-tracking-parent={CREATIVE_ID.CTA}
              />
              {!!totalInventory && (
                <div className="small fst-italic mt-0_25 text-blue-40">{totalInventory} for sale near you</div>
              )}
            </Fragment>
          ) : (
            <Fragment>
              {isDealerlessOem && (
                <div className="text-start text-gray-darker mb-1 mt-2">
                  Choose the trim, color, options, packages and more for your {vehicleName}.
                </div>
              )}
              <Button
                {...buttonProps}
                className={classnames('summary-cta', buttonProps.className, {
                  'mt-2': !isDealerlessOem,
                })}
              />
            </Fragment>
          )}
        </Fragment>
      )}
      <div
        className="medium mt-1 mb-0_5 text-center"
        data-tracking-id="click_tariff_info"
        data-tracking-value={finalAssemblyCountryText}
      >
        Edmunds suggested price is based on sales in {stateCode} thru{' '}
        {dateFormat(getEspTrustDateUnformatted(), 'm/d/yy')}, including destination fee, but not other dealer fees.
        {!!finalAssemblyCountryText && <Fragment> {finalAssemblyCountryText}.</Fragment>}
      </div>
    </div>
  );
}

TrimsCtaSimpleSummaryUI.propTypes = {
  className: PropTypes.string,
  ctaProps: PropTypes.shape({
    onClick: PropTypes.func,
    to: PropTypes.string,
    rel: PropTypes.string,
    tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    'data-tracking-id': PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  }),
  selectedTrim: InventoryTrimSummaryEntities.TrimSummary.isRequired,
  vehicleName: PropTypes.string.isRequired,
  vehicleParams: PropTypes.shape({
    makeSlug: PropTypes.string,
    makeName: PropTypes.string,
    modelSlug: PropTypes.string,
    modelName: PropTypes.string,
    submodelSlug: PropTypes.string,
    year: PropTypes.string,
  }).isRequired,
  finalAssemblyCountry: PropTypes.string,
  belowEspLeadTargetsNum: PropTypes.number,
  stateCode: PropTypes.string,
  srpLink: PropTypes.string,
  showLeadForm: PropTypes.bool,
  isDealerlessOem: PropTypes.bool,
};

export const TrimsCtaSimpleSummary = connectToModel(TrimsCtaSimpleSummaryUI, {
  finalAssemblyCountry: bindToPath(
    ({ vehicleParams: { makeSlug, modelSlug, submodelSlug, year } }) =>
      FeatureSpecsPaths.buildPartialFeaturesPath({ make: makeSlug, model: modelSlug, submodel: submodelSlug, year }),
    VehicleModel,
    (stylesFeatures, { selectedTrim }) =>
      getFinalAssemblyCountry(stylesFeatures.find(({ id }) => id === selectedTrim.styleId))
  ),
  belowEspLeadTargetsNum: bindToPath(
    ({ selectedTrim, vehicleParams: { makeName, modelName, submodelSlug, year }, isCore7026Enabled }) => {
      if (!isCore7026Enabled) {
        return false;
      }

      const strategy = isRoiTargetedFlavor() ? STRATEGIES.TRIM_DLINV_DST_TARGET_ROI : STRATEGIES.TRIM_DLINV_DST;

      return buildLeadTargetsPath({
        make: makeName,
        model: modelName,
        year,
        styleId: selectedTrim.styleId,
        strategy,
        radius: 100,
        apiSubModel: submodelSlug,
        encodedTrim: selectedTrim.trimName,
      });
    },
    LeadTargetsModel,
    (leadTargets, { selectedTrim }) =>
      leadTargets?.length && selectedTrim?.pricing?.suggestedPrice
        ? leadTargets.filter(
            leadTarget =>
              leadTarget?.inventory?.prices?.displayPrice &&
              leadTarget.inventory.prices.displayPrice < selectedTrim.pricing.suggestedPrice
          ).length
        : 0
  ),
  stateCode: bindToPath('location.stateCode', VisitorModel),
});
